import { useContext } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import {
  GroupedCashFlow,
  CashFlowCollection as ICashFlowCollection,
  GroupedCashFlows,
  TransactionType,
} from '@algoan/dashboard/model';
import { CashFlowRow } from './cash-flow-row';
import { CashFlowCollectionExpandedContext } from './cash-flow-collection-expanded-context';
import { useTransactionCategoryTranslator, useTransactionTypeTranslator } from '../../messages';
import { cashFlowHasLabel } from '@algoan/dashboard/utils';
import { ArrowIcon } from '../icons';

interface CashFlowCollectionProps {
  calendar: GroupedCashFlows['calendar'];
  cashFlowCollection: ICashFlowCollection;
  checkable: boolean;
  headerExists?: boolean;
  isEven: boolean;
  dataTestId?: string;
}

const cashFlowTypesToDisplay = [TransactionType.CARD, TransactionType.CHECK, TransactionType.CASH_WITHDRAWAL];

export const CashFlowCollection: React.FC<CashFlowCollectionProps> = ({
  calendar,
  cashFlowCollection,
  checkable,
  headerExists,
  isEven,
  dataTestId,
}) => {
  const context = useContext(CashFlowCollectionExpandedContext);
  const isExpanded = context.isExpanded(cashFlowCollection.category);
  const translateTransactionCategory = useTransactionCategoryTranslator();
  const translateTransactionType = useTransactionTypeTranslator();

  const getCashFlowLabel = (cashFlow: GroupedCashFlow): React.ReactNode => {
    const labelStrategy = cashFlow.labelStrategy ?? 'default';
    const category = cashFlow.category;
    const type = cashFlow.type;
    if (labelStrategy === 'default') {
      if (cashFlowHasLabel(cashFlow)) {
        return cashFlow.label.toLowerCase();
      } else if (type && cashFlowTypesToDisplay.includes(type)) {
        return translateTransactionType(type);
      }

      return (
        <FormattedMessage
          id="widgets.regular_cash_flows_analysis.other_label"
          defaultMessage="others non regular transactions"
        />
      );
    } else {
      switch (labelStrategy) {
        case 'onlyCategory':
          return translateTransactionCategory(category);
        case 'categoryAndType':
          return `${translateTransactionCategory(category)} - ${translateTransactionType(type)}`;
        case 'labelRoot':
          return cashFlow.labelRoot?.toLowerCase();
      }
    }
  };

  return (
    <>
      <S.CashFlowRow
        calendar={calendar}
        cashFlow={cashFlowCollection}
        checkable={checkable}
        headerExists={headerExists}
        $isExpanded={isExpanded}
        isEven={isEven}
        firstColumn={
          <S.CashFlowCollectionTitle onClick={() => context.toggleExpansion(cashFlowCollection.category)}>
            <S.ExpandButton>
              <ArrowIcon direction={isExpanded ? 'down' : 'right'} />
            </S.ExpandButton>
            <strong>{translateTransactionCategory(cashFlowCollection.category)}</strong>
          </S.CashFlowCollectionTitle>
        }
        dataTestId={dataTestId}
      />
      {cashFlowCollection.cashFlows.map((subCashFlow, subCashFlowIndex) => (
        <S.SubCashFlowRow
          calendar={calendar}
          cashFlow={subCashFlow}
          checkable={checkable}
          headerExists={headerExists}
          isEven={isEven}
          $isFirst={subCashFlowIndex === 0}
          $isLast={subCashFlowIndex === cashFlowCollection.cashFlows.length - 1}
          firstColumn={getCashFlowLabel(subCashFlow)}
          key={`sub-cash-flow-${subCashFlowIndex}`}
          $hide={!isExpanded}
        />
      ))}
    </>
  );
};

const S = {
  CashFlowRow: styled(CashFlowRow)<{ $isExpanded: boolean }>`
    ${(props) =>
      props.$isExpanded
        ? `
        td {
          &:first-child {
            border-bottom-left-radius: 0;
          }

          &:last-child {
            border-bottom-right-radius: 0;
          }
        }
    `
        : ''}
  `,
  ExpandButton: styled.div`
    margin-left: -12px;
    margin-right: 6px;
    display: inline-block;
  `,
  CashFlowCollectionTitle: styled.div`
    display: inline-block;

    &:hover {
      cursor: pointer;
    }
  `,
  SubCashFlowRow: styled(CashFlowRow)<{ $isFirst: boolean; $isLast: boolean; isEven: boolean; $hide: boolean }>`
    td {
      ${(props) => (props.$isFirst && !props.$hide ? 'border-top: 1px solid white;' : 'border-top: 0 solid white;')}
      ${(props) => (props.$hide ? 'padding: 0;' : '')}
      transition: border-top 300ms ease-in-out;

      & > div {
        box-sizing: border-box;
        overflow: hidden;
        transition: max-height 500ms ease;
        max-height: ${(props) => (props.$hide ? '0' : '60px')};
      }

      &:first-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: ${(props) => (props.$isLast ? '4px' : '0')};
        padding-left: 35px;
      }

      &:last-child {
        border-top-right-radius: 0;
        border-bottom-right-radius: ${(props) => (props.$isLast ? '4px' : '0')};
      }
    }
  `,
};
